// ════════════════════════════════════════════════════════════════════
// W'ILY'S PIER — buildKit.jsx   ·   THE BUILD-MODE CHROME, SHARED
// ────────────────────────────────────────────────────────────────────
// (2026-09-15) The yard and the Hall each had their own shed panel —
// two text lists, no thumbnails, no headers, no blurb — and their own
// held bar, key handling and undo (none). Every cozy building game
// caP'n named this against has ONE build UI that works the same in
// every room. This file is that: the pieces both screens mount, so a
// player learns the shed once.
//
// What lives here:
//   isTypingTarget(e)   — the one answer to "should a hotkey fire?"
//   useUndo()           — a small stack of reversible build actions
//   Thumb               — a piece's picture, from the 2D kit
//   ShedPanel           — the shed, grouped by what things DO, with pictures
//   OnboardCard         — the first-visit card, dismissable, remembered
//   ensureBuildKeyframes — the +✹ burst animation (id-guarded, never JSX <style>)
//   GHOST_VERB          — one vocabulary for free / stack / blocked
//
// Everything reads `theme.js` (L55: one dim, one gold). The Hall's
// older SD chrome is not extended here — the shed is the same panel in
// both rooms on purpose.
// ════════════════════════════════════════════════════════════════════

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ITEMS, KIND, SHELF_META, LAYER, layerOf, sizeOf, sizeLabel } from "./mineriumCatalogue";
import { MineriumProp2D } from "./mineriumProps2D";
import { UI, uiPanel, uiBtn, uiLabel } from "./theme";
import { isMobile } from "./PlayerEntity";
import { isMuted, toggleMuted, sfx } from "./pierSfx";

export const UD = "✹";
export const fmtUD = (n) => (n || 0).toLocaleString();

// ── the one answer to "is the player typing?" ───────────────────────
// Both screens' hotkeys (E, R, B, Z, WASD) fired while a friend code was
// being typed into the social dock — `b` toggled build mode, `e` placed
// a chair, `a`/`d` walked. One helper, used by every key listener.
export const isTypingTarget = (e) =>
  !!e?.target?.closest?.('input,textarea,select,[contenteditable="true"]');

// ── the ghost's vocabulary ──────────────────────────────────────────
export const GHOST_VERB = {
  free:    { key: "E", label: "PUT IT DOWN", tint: UI.ok },
  stack:   { key: "E", label: "BALANCE IT", tint: UI.goldHi },
  blocked: { key: "—", label: "CAN'T GO THERE", tint: UI.err },
};

// ── undo ────────────────────────────────────────────────────────────
// A stack of the last few reversible build actions. Each entry is
// { type: "place" | "pickup" | "move", ...what it takes to reverse it }.
// The screen supplies the reversers, because they are its writers.
// ⚠ THE STACK IS A REF, and state only re-renders (review, 2026-09-15). A
// `setState` updater is run lazily whenever the fiber has any pending
// update — the 15 Hz position mirror, a presence tick — so `pop()` written
// as an updater returned null while the entry was still sliced away.
const RECOVERABLE = new Set(["occupied", "reserved", "no_room", "contested", "sealed", "unharvested"]);
export function useUndo(limit = 12) {
  const ref = useRef([]);
  const [, bump] = useState(0);
  const push = useCallback((entry) => { ref.current = [...ref.current.slice(-(limit - 1)), entry]; bump((v) => v + 1); }, [limit]);
  const pop = useCallback(() => {
    const top = ref.current[ref.current.length - 1] || null;
    ref.current = ref.current.slice(0, -1);
    bump((v) => v + 1);
    return top;
  }, []);
  const clear = useCallback(() => { ref.current = []; bump((v) => v + 1); }, []);
  // ⚠⚠ OW-34 E2E review. Undoing a PICK UP puts the piece back under a NEW
  // propId (push() never reissues a key), and every older entry still named
  // the old one — so one undo of a pick-up killed the rest of the stack.
  // `remap` renames it throughout. And a refused undo is handed BACK
  // (`restore`), not thrown away: "the spot is taken" is a reason to try
  // again after clearing it, not to forget the step.
  const remap = useCallback((fromId, toId) => {
    if (!fromId || !toId || fromId === toId) return;
    ref.current = ref.current.map((e) => ({
      ...e,
      ...(e.propId === fromId ? { propId: toId } : {}),
      ...(e.on === fromId ? { on: toId } : {}),       // a torch's entry names the barrel it stood on
    }));
  }, []);
  // ⚠ only a refusal that can CLEAR goes back — a piece that no longer
  // exists, or stock that is gone, would sit on top forever and block every
  // older step (verify review)
  const restore = useCallback((entry, result) => {
    if (!entry || !RECOVERABLE.has(result?.reason)) return false;
    ref.current = [...ref.current, entry]; bump((v) => v + 1);
    return true;
  }, []);
  const stack = ref.current;
  const last = stack[stack.length - 1] || null;
  // a stable object while nothing changed, so handlers that close over it
  // are not rebuilt on every step the player takes
  return useMemo(() => ({ stack, push, pop, clear, remap, restore, canUndo: stack.length > 0, last }),
    [stack, push, pop, clear, remap, restore, last]);
}

// ── a piece's picture ───────────────────────────────────────────────
// The 2D kit draws inside a tile-sized box; a thumbnail is that box.
export function Thumb({ itemId, size = 40, tint }) {
  const item = ITEMS[itemId];
  const shelf = (SHELF_META[item?.shelf] || SHELF_META.fable).tint;
  return (
    <div style={{
      position: "relative", width: size, height: size, flex: "none",
      borderRadius: UI.rSm, overflow: "hidden",
      background: `radial-gradient(circle at 50% 70%, ${tint || shelf}22, ${UI.panelSolid} 70%)`,
      border: `1px solid ${UI.edge}`,
    }}>
      <MineriumProp2D itemId={itemId} tile={size} />
    </div>
  );
}

// what a piece DOES, for the shed's headers
const KIND_LABEL = {
  [KIND.GROWER]: "GROWERS · they pay Dawgs",
  [KIND.PROP]:   "FURNISHINGS",
  [KIND.DOOR]:   "DOORS",
};
// ⭐ OW-34: furnishings split by layer, so a shed with thirty kinds of
// piece still reads — what stands, what lies underfoot, what hangs.
KIND_LABEL[`${KIND.PROP}:${LAYER.FLOOR}`] = "UNDERFOOT · rugs, runners, paths";
KIND_LABEL[`${KIND.PROP}:${LAYER.WALL}`]  = "ON THE WALLS · face a plain wall";
const KIND_ORDER = [KIND.GROWER, KIND.PROP, `${KIND.PROP}:${LAYER.FLOOR}`, `${KIND.PROP}:${LAYER.WALL}`, KIND.DOOR];
const shedGroupOf = (it) => (it.kind === KIND.PROP && layerOf(it.id) !== LAYER.STAND ? `${KIND.PROP}:${layerOf(it.id)}` : it.kind);

// where the shed sits on a phone — the prompts and held bars clear it
export const SHED_MOBILE_BOTTOM = 118;
export const SHED_MOBILE_MAXH = "40vh";
// a bar that must sit ABOVE the mobile shed
export const ABOVE_SHED_MOBILE = `calc(${SHED_MOBILE_BOTTOM}px + ${SHED_MOBILE_MAXH} + 8px)`;

// ── THE SHED ────────────────────────────────────────────────────────
// Grouped by kind, with a picture, a count and the shelf tint. Tap a row
// to hold the piece; tap again to put it away. The selected piece's blurb
// and footprint show under the list, so nobody places a two-tile pool
// without knowing it is two tiles.
export function ShedPanel({
  shed, held, busy, onPick, onClose, title = "THE SHED",
  canUndo = false, onUndo = null, footer = null, style = {},
  emptyLine = "Nothing put by. Ally sells bundles — that is the cheap way to fill a place.",
}) {
  const groups = useMemo(() => {
    const by = {};
    for (const it of shed) (by[shedGroupOf(it)] ||= []).push(it);
    return KIND_ORDER.filter((k) => by[k]?.length).map((k) => ({ kind: k, label: KIND_LABEL[k] || k.toUpperCase(), items: by[k] }));
  }, [shed]);
  const heldItem = held ? ITEMS[held] : null;
  const [mute, setMute] = useState(isMuted());
  const mobile = isMobile();

  return (
    <div style={{
      ...uiPanel(),
      position: "fixed", zIndex: 600, backdropFilter: "blur(8px)",
      display: "flex", flexDirection: "column",
      left: 12,
      ...(mobile
        ? { right: 190, bottom: SHED_MOBILE_BOTTOM, maxHeight: SHED_MOBILE_MAXH }
        : { width: 272, bottom: 62, maxHeight: "56vh" }),
      ...style,
    }}>
      <div style={{ padding: "9px 11px", borderBottom: `1px solid ${UI.edge}`,
                    display: "flex", alignItems: "center", gap: 8 }}>
        <span style={{ ...uiLabel, color: UI.gold }}>{title}</span>
        <span style={{ ...uiLabel }}>{shed.reduce((a, i) => a + i.count, 0)} pieces</span>
        <div style={{ flex: 1 }} />
        <button title={mute ? "unmute" : "mute"} onClick={() => { setMute(toggleMuted()); }}
                style={{ ...uiBtn(UI.dim), padding: "3px 7px" }}>{mute ? "🔇" : "🔊"}</button>
        {onUndo && (
          <button disabled={!canUndo || busy} onClick={onUndo}
                  style={{ ...uiBtn(UI.gold), padding: "3px 8px", opacity: canUndo ? 1 : 0.4 }}>UNDO · Z</button>
        )}
        {onClose && <button onClick={onClose} style={{ ...uiBtn(UI.dim), padding: "3px 8px" }}>×</button>}
      </div>

      {/* ⚑ the piece list keeps a floor under it — a tall footer (the Hall's
          LAYOUTS & DESIGNS, BUILD ROOMS) scrolls in its own box instead of
          squeezing the list to nothing on a phone (OW-42 review) */}
      <div style={{ overflowY: "auto", padding: 7, flex: 1, minHeight: mobile ? 44 : 90 }}>
        {shed.length === 0 && (
          <div style={{ padding: 12, fontSize: 11, color: UI.dim, lineHeight: 1.6 }}>{emptyLine}</div>
        )}
        {groups.map((g) => (
          <div key={g.kind} style={{ marginBottom: 6 }}>
            <div style={{ ...uiLabel, padding: "5px 4px 4px", color: UI.dim }}>{g.label}</div>
            {g.items.map((it) => {
              const on = held === it.id;
              const tint = (SHELF_META[it.shelf] || SHELF_META.fable).tint;
              return (
                <button key={it.id} disabled={busy}
                  onClick={() => { sfx.ui(); onPick(on ? null : it.id); }}
                  style={{ ...uiBtn(tint, { on }), width: "100%", textAlign: "left",
                           display: "flex", alignItems: "center", gap: 9, marginBottom: 4, padding: "5px 7px" }}>
                  <Thumb itemId={it.id} size={34} tint={tint} />
                  <span style={{ flex: 1, minWidth: 0 }}>
                    <span style={{ display: "block", fontSize: 11.5, fontWeight: 700, color: UI.text,
                                   whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{it.name}</span>
                    <span style={{ display: "block", fontSize: 10, color: UI.dim, marginTop: 1 }}>
                      {sizeOf(it.id).join() === "1,1" ? "1 tile" : `${sizeLabel(it.id)} tiles`}
                      {layerOf(it.id) === LAYER.FLOOR ? " · underfoot" : layerOf(it.id) === LAYER.WALL ? " · hangs on a wall" : ""}
                      {it.grow ? ` · ${UD}${it.grow.yieldUD} / ${Math.round(it.grow.ripenMs / 60000)} min` : ""}
                      {it.stackTop != null ? " · holds a thing" : ""}
                    </span>
                  </span>
                  <span style={{ color: tint, fontSize: 12, fontWeight: 700 }}>×{it.count}</span>
                </button>
              );
            })}
          </div>
        ))}
      </div>

      {heldItem && (
        <div style={{ borderTop: `1px solid ${UI.edge}`, padding: "8px 11px", fontSize: 10.5, color: UI.dim, lineHeight: 1.55 }}>
          <b style={{ color: UI.text }}>{heldItem.name}</b> — {heldItem.blurb}
        </div>
      )}
      {footer && <div style={{ overflowY: "auto", flexShrink: 1, minHeight: 0, maxHeight: mobile ? "62%" : "58%" }}>{footer}</div>}
    </div>
  );
}

// ── THE FIRST VISIT ─────────────────────────────────────────────────
// A card, not a flash. The old onboarding was one line for 3.8 seconds,
// once, ever — and it did not mention a single key. This stays until it
// is dismissed and is remembered per player per room.
const onboardKey = (uid, room) => `ie_minerium_onboarded_${room}_${uid || "anon"}`;
export const wasOnboarded = (uid, room) => { try { return localStorage.getItem(onboardKey(uid, room)) === "1"; } catch { return true; } };
export const markOnboarded = (uid, room) => { try { localStorage.setItem(onboardKey(uid, room), "1"); } catch { /* fine */ } };

// `after` — a follow-up card (what is NEW) waits until the player has
// dismissed the room's first card, so a brand-new player never gets two
// stacked on each other, and a returning one still hears about the new.
export function OnboardCard({ uid, room, lines, onDismiss, title = "YOUR MINERIUM", after = null }) {
  const [show, setShow] = useState(() => !wasOnboarded(uid, room) && (!after || wasOnboarded(uid, after)));
  if (!show) return null;
  const mobile = isMobile();
  const done = () => { markOnboarded(uid, room); setShow(false); sfx.ui(); onDismiss?.(); };
  return (
    <div style={{
      ...uiPanel(), position: "fixed", zIndex: 650, backdropFilter: "blur(10px)",
      top: mobile ? 90 : 96, left: "50%", transform: "translateX(-50%)",
      width: "min(440px, calc(100vw - 40px))", padding: "14px 16px",
      border: `1px solid ${UI.gold}66`,
    }}>
      <div style={{ fontSize: 13, letterSpacing: ".16em", color: UI.gold, fontWeight: 700, marginBottom: 8 }}>{title}</div>
      <div style={{ display: "grid", gridTemplateColumns: "auto 1fr", gap: "6px 12px", fontSize: 11.5, lineHeight: 1.5 }}>
        {lines.map(([k, v]) => (
          <div key={k} style={{ display: "contents" }}>
            <span style={{ color: UI.goldHi, fontWeight: 700, whiteSpace: "nowrap" }}>{k}</span>
            <span style={{ color: UI.text }}>{v}</span>
          </div>
        ))}
      </div>
      <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 12 }}>
        <button onClick={done} style={uiBtn(UI.gold, { on: true })}>GOT IT</button>
      </div>
    </div>
  );
}

// ── THE BURST ───────────────────────────────────────────────────────
// +✹N rising off the grower you just collected. Keyframes go through an
// id-guarded style element, never a JSX <style> block (the J'aeonxi trap).
export function ensureBuildKeyframes() {
  if (typeof document === "undefined") return;
  const ID = "pier-build-keyframes";
  if (document.getElementById(ID)) return;
  const el = document.createElement("style");
  el.id = ID;
  el.textContent = `
    @keyframes ieBurstUp { 0%{transform:translate(-50%,0) scale(.8);opacity:0} 15%{transform:translate(-50%,-6px) scale(1.1);opacity:1} 100%{transform:translate(-50%,-46px) scale(1);opacity:0} }
    @keyframes ieSettle { 0%{transform:scale(.7)} 60%{transform:scale(1.06)} 100%{transform:scale(1)} }
    @keyframes ieRipePulse { 0%,100%{opacity:.55;transform:scale(1)} 50%{opacity:1;transform:scale(1.12)} }
  `;
  document.head.appendChild(el);
}

export const burstStyle = {
  fontFamily: UI.font, fontSize: 15, fontWeight: 800, color: UI.ok,
  textShadow: `0 0 12px ${UI.ok}88, 0 2px 6px #000c`, whiteSpace: "nowrap",
  animation: "ieBurstUp 1.4s ease-out forwards", pointerEvents: "none",
  position: "absolute", left: "50%",
};

// the +✹ bursts a screen keeps: push one, it removes itself
export function useBursts() {
  const [bursts, setBursts] = useState([]);
  const n = useRef(0);
  useEffect(() => { ensureBuildKeyframes(); }, []);
  const burst = useCallback((x, y, amount) => {
    const id = ++n.current;
    setBursts((b) => [...b, { id, x, y, amount }]);
    setTimeout(() => setBursts((b) => b.filter((e) => e.id !== id)), 1500);
  }, []);
  return { bursts, burst };
}

const buildKitApi = { isTypingTarget, GHOST_VERB, useUndo, Thumb, ShedPanel, OnboardCard, ensureBuildKeyframes, burstStyle, useBursts };
export default buildKitApi;
